import { EffectsSelector } from './EffectsSelector';
import { RatingInput } from './RatingInput';
import { RecipeSelector } from './RecipeSelector';
import type { JournalEntryFormData, JournalValidationError } from '../../types/journal';
import type { Recipe } from '../../types/recipe';

interface EntryFormProps {
  formData: JournalEntryFormData;
  validationErrors: JournalValidationError[];
  isSubmitting: boolean;
  submitError: string | null;
  showRecipeSelector: boolean;
  recipes: Recipe[] | null;
  recipesLoading: boolean;
  recipesError: string | null;
  onFieldChange: (field: keyof JournalEntryFormData, value: any) => void;
  onEffectsChange: (effects: string[]) => void;
  onRatingChange: (rating: number | null) => void;
  onToggleRecipeSelector: () => void;
  onRecipeSelect: (recipe: Recipe) => void;
  onRecipeSelectorClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

function getFieldError(
  errors: JournalValidationError[],
  field: keyof JournalEntryFormData
): string | undefined {
  return errors.find((e) => e.field === field)?.message;
}

export function EntryForm({
  formData,
  validationErrors,
  isSubmitting,
  submitError,
  showRecipeSelector,
  recipes,
  recipesLoading,
  recipesError,
  onFieldChange,
  onEffectsChange,
  onRatingChange,
  onToggleRecipeSelector,
  onRecipeSelect,
  onRecipeSelectorClose,
  onSubmit,
}: EntryFormProps) {
  const servingSizeError = getFieldError(validationErrors, 'servingSize');
  const cannabinoidMgError = getFieldError(validationErrors, 'cannabinoidMg');
  const cannabinoidTypeError = getFieldError(validationErrors, 'cannabinoidType');
  const consumedAtError = getFieldError(validationErrors, 'consumedAt');
  const onsetMinutesError = getFieldError(validationErrors, 'onsetMinutes');
  const peakMinutesError = getFieldError(validationErrors, 'peakMinutes');
  const durationMinutesError = getFieldError(validationErrors, 'durationMinutes');
  const effectsError = getFieldError(validationErrors, 'effects');
  const notesError = getFieldError(validationErrors, 'notes');
  const ratingError = getFieldError(validationErrors, 'rating');

  return (
    <form onSubmit={onSubmit} noValidate>
      {/* Serving Size */}
      <div className="form-group">
        <label htmlFor="entry-servingSize">Serving Size</label>
        <input
          id="entry-servingSize"
          type="number"
          value={formData.servingSize}
          onChange={(e) => onFieldChange('servingSize', e.target.value)}
          min={0.01}
          max={999.99}
          step="any"
          required
          aria-invalid={!!servingSizeError}
          aria-describedby={servingSizeError ? 'error-servingSize' : undefined}
        />
        {servingSizeError && (
          <span id="error-servingSize" className="field-error" role="alert">
            {servingSizeError}
          </span>
        )}
      </div>

      {/* Cannabinoid Amount (mg) */}
      <div className="form-group">
        <label htmlFor="entry-cannabinoidMg">Cannabinoid Amount (mg)</label>
        <input
          id="entry-cannabinoidMg"
          type="number"
          value={formData.cannabinoidMg}
          onChange={(e) => onFieldChange('cannabinoidMg', e.target.value)}
          min={0}
          max={10000}
          step="any"
          required
          aria-invalid={!!cannabinoidMgError}
          aria-describedby={cannabinoidMgError ? 'error-cannabinoidMg' : undefined}
        />
        {cannabinoidMgError && (
          <span id="error-cannabinoidMg" className="field-error" role="alert">
            {cannabinoidMgError}
          </span>
        )}
      </div>

      {/* Cannabinoid Type */}
      <div className="form-group">
        <label htmlFor="entry-cannabinoidType">Cannabinoid Type</label>
        <input
          id="entry-cannabinoidType"
          type="text"
          value={formData.cannabinoidType}
          onChange={(e) => onFieldChange('cannabinoidType', e.target.value)}
          maxLength={50}
          required
          aria-invalid={!!cannabinoidTypeError}
          aria-describedby={cannabinoidTypeError ? 'error-cannabinoidType' : undefined}
        />
        {cannabinoidTypeError && (
          <span id="error-cannabinoidType" className="field-error" role="alert">
            {cannabinoidTypeError}
          </span>
        )}
      </div>

      {/* Consumed At */}
      <div className="form-group">
        <label htmlFor="entry-consumedAt">Date & Time Consumed</label>
        <input
          id="entry-consumedAt"
          type="datetime-local"
          value={formData.consumedAt}
          onChange={(e) => onFieldChange('consumedAt', e.target.value)}
          required
          aria-invalid={!!consumedAtError}
          aria-describedby={consumedAtError ? 'error-consumedAt' : undefined}
        />
        {consumedAtError && (
          <span id="error-consumedAt" className="field-error" role="alert">
            {consumedAtError}
          </span>
        )}
      </div>

      {/* Onset Minutes */}
      <div className="form-group">
        <label htmlFor="entry-onsetMinutes">Onset Time (minutes)</label>
        <input
          id="entry-onsetMinutes"
          type="number"
          value={formData.onsetMinutes ?? ''}
          onChange={(e) =>
            onFieldChange('onsetMinutes', e.target.value === '' ? null : e.target.value)
          }
          min={0}
          max={1440}
          step={1}
          aria-invalid={!!onsetMinutesError}
          aria-describedby={onsetMinutesError ? 'error-onsetMinutes' : undefined}
        />
        {onsetMinutesError && (
          <span id="error-onsetMinutes" className="field-error" role="alert">
            {onsetMinutesError}
          </span>
        )}
      </div>

      {/* Peak Minutes */}
      <div className="form-group">
        <label htmlFor="entry-peakMinutes">Peak Time (minutes)</label>
        <input
          id="entry-peakMinutes"
          type="number"
          value={formData.peakMinutes ?? ''}
          onChange={(e) =>
            onFieldChange('peakMinutes', e.target.value === '' ? null : e.target.value)
          }
          min={0}
          max={1440}
          step={1}
          aria-invalid={!!peakMinutesError}
          aria-describedby={peakMinutesError ? 'error-peakMinutes' : undefined}
        />
        {peakMinutesError && (
          <span id="error-peakMinutes" className="field-error" role="alert">
            {peakMinutesError}
          </span>
        )}
      </div>

      {/* Duration Minutes */}
      <div className="form-group">
        <label htmlFor="entry-durationMinutes">Duration (minutes)</label>
        <input
          id="entry-durationMinutes"
          type="number"
          value={formData.durationMinutes ?? ''}
          onChange={(e) =>
            onFieldChange('durationMinutes', e.target.value === '' ? null : e.target.value)
          }
          min={0}
          max={1440}
          step={1}
          aria-invalid={!!durationMinutesError}
          aria-describedby={durationMinutesError ? 'error-durationMinutes' : undefined}
        />
        {durationMinutesError && (
          <span id="error-durationMinutes" className="field-error" role="alert">
            {durationMinutesError}
          </span>
        )}
      </div>

      {/* Effects */}
      <div className="form-group">
        <EffectsSelector selected={formData.effects} onChange={onEffectsChange} />
        {effectsError && (
          <span id="error-effects" className="field-error" role="alert">
            {effectsError}
          </span>
        )}
      </div>

      {/* Notes */}
      <div className="form-group">
        <label htmlFor="entry-notes">Notes</label>
        <textarea
          id="entry-notes"
          value={formData.notes}
          onChange={(e) => onFieldChange('notes', e.target.value)}
          maxLength={2000}
          rows={4}
          aria-invalid={!!notesError}
          aria-describedby={notesError ? 'error-notes' : undefined}
        />
        {notesError && (
          <span id="error-notes" className="field-error" role="alert">
            {notesError}
          </span>
        )}
      </div>

      {/* Rating */}
      <div className="form-group">
        <span className="form-label" id="entry-rating-label">
          Rating
        </span>
        <RatingInput value={formData.rating} onChange={onRatingChange} />
        {ratingError && (
          <span id="error-rating" className="field-error" role="alert">
            {ratingError}
          </span>
        )}
      </div>

      {/* Recipe Link */}
      <div className="form-group">
        <button
          type="button"
          className="cta-button"
          onClick={onToggleRecipeSelector}
        >
          {formData.recipeName ? `Linked: ${formData.recipeName}` : 'Link Recipe'}
        </button>
      </div>

      <RecipeSelector
        isOpen={showRecipeSelector}
        recipes={recipes}
        isLoading={recipesLoading}
        error={recipesError}
        onSelect={onRecipeSelect}
        onClose={onRecipeSelectorClose}
      />

      {submitError && (
        <div role="alert" className="form-submit-error">
          <span className="field-error">{submitError}</span>
        </div>
      )}

      <button
        type="submit"
        className="cta-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Saving...' : 'Log Entry'}
      </button>
    </form>
  );
}
